/**
 * Game Component for Lobby2.0
 * PERFORMANCE OPTIMIZED: Uses CSS modules for instant rendering
 */

import React, { memo, useCallback, useMemo } from 'react';
import { clsx } from 'clsx';
import type IGameSettings from '../../../../types/models/IGameSettings';
import {
  useViewMode,
  useCurrency,
  usePlayerCountForGame,
} from 'store/app/appStore';
import { useIsFavorite } from '../../../favorites/store/favoritesStore';
import { useGameCupStatus } from '../../../campaigns/store/tournamentCupStore';
import {
  useEffectiveGameData,
  useFavoriteToggle,
  useGameImageUrls,
  useGameStartActions,
  useIsStartingGame,
  useIsWaitingForDefenseCode,
  useShouldBranding,
} from 'features/games';
import { useGameSounds } from 'shared/hooks';
import { clickCounter } from 'core/services/EventTracker';
import { transactionTracker } from 'core/services/TransactionTracker';
import {
  SPJ_JACKPOT_GAMES,
  SECTION_NAMES,
  ALL_JP_BADGES,
} from '@repo/common';
import Image from '../../../../shared/components/Image';
import GameThumbnail from './components/GameComponents/GameThumbnail';
import {
  ButtonFavorites,
  ButtonPlay,
  CrashGameLabel,
  TagsContainer,
  MinMaxBet,
  CurrentPlayersCount,
  JackpotBottomBand,
  TopGamesNumbers,
  CampaignLogo,
  GameBanner,
} from './components';

import { type SINGLE_VIEW_MODE, MULTI_VIEW_MODE, CRASH_GAMES } from '../../../../constants';

import TopGamesNumbersStyles from './components/TopGamesNumbers/TopGamesNumbers.module.css';
import { getTopGameNumberLayout } from './components/TopGamesNumbers/topGamesDigits';
import styles from './Game.module.css';
import { getBadgeCSSVars, getBadgeSectionName } from './config/sectionBadgeRegistry';
import { getNoJackpotException, getJackpotSlug } from './utils/jackpotBandLabel';

interface GameProps {
  data: IGameSettings;
  sectionName: string;
  cols?: number;
  rows?: number;
  displayIndex?: number;
  viewMode?: typeof SINGLE_VIEW_MODE | typeof MULTI_VIEW_MODE;
  context?: Record<string, unknown>;
  sectionGames?: unknown[]; // Add section games for progressive denomination calculation
}

const Game: React.FC<GameProps> = ({
  data,
  sectionName,
  context: _context,
  cols = 1,
  rows = 2,
  displayIndex,
  viewMode: viewModeProp,
  sectionGames,
}) => {
  const currency = useCurrency();
  const globalViewMode = useViewMode();
  const viewMode = viewModeProp || globalViewMode;
  const { startGame } = useGameStartActions();
  const isStartingGame = useIsStartingGame();
  const isWaitingForDefenseCode = useIsWaitingForDefenseCode();
  const { playHoverSound, playSelectGameSound } = useGameSounds();

  // In the Premium Link section the games arrive with the group jackpotName
  // ("Premium Link"). Swap it for the individual jackpot the game belongs to
  // (resolved from the jackpot instances) so badges and the bottom-band value
  // are per game. Everything else on the game stays untouched.
  const { effectiveData, effectiveSectionName } = useEffectiveGameData(data, sectionName);

  // Top ranking numbers key off sectionName (not effectiveSectionName): the Premium Link override would drop them for Premium Link games listed in Top Games (ON-170228).
  const showTopGames = sectionName === SECTION_NAMES.TOP && viewMode === MULTI_VIEW_MODE;
  const topRank = displayIndex || data.order;

  // Get real current players data from store
  const currentPlayers = usePlayerCountForGame(data.gameKey);

  // PERFORMANCE OPTIMIZED: Only re-renders when THIS game's favorite status changes
  const isFavorite = useIsFavorite(data.gameKey);

  // Check if this game should show tournament cup (lightweight store)
  const shouldShowTournamentCup = useGameCupStatus(data.gameKey);

  const minDenomination = useMemo(() => data.minDenomination.get(currency) || 0, [data.minDenomination, currency]);

  const shouldBranding = useShouldBranding(data, effectiveData);
  // Memoized image URLs to avoid recalculation
  const imageUrls = useGameImageUrls(data, effectiveData, effectiveSectionName, shouldBranding);

  // PERFORMANCE OPTIMIZED: Minimal dependencies to prevent mass re-renders
  const handleFavoriteClick = useFavoriteToggle(data);

  const handleGameClick = useCallback(() => {
    // Track with consistent naming: click_game_{GAME_KEY}_{view_mode}_{section}
    const sectionKey = sectionName.toLowerCase().replace(/\s+/g, '_');
    const viewModeKey = viewMode === 'multi' ? 'multi' : 'single';
    clickCounter.trackEvent(`click_game_${data.gameKey}_${viewModeKey}_${sectionKey}`);

    // Track game open transaction for Sentry
    transactionTracker.trackGameOpen(data.name, data.gameKey, sectionName, viewMode);

    // Play game selection sound (matches old lobby behavior)
    playSelectGameSound();

    // Start the game using current action reference
    startGame(data.gameKey);
  }, [data.gameKey, data.name, sectionName, viewMode, startGame, playSelectGameSound]);

  const handleClickMobile = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isStartingGame || isWaitingForDefenseCode) {
        return;
      }
      if (!(e.target as HTMLElement).classList.contains('egt_game_btn_favorite')) {
        handleGameClick();
      }
    },
    [handleGameClick, isStartingGame, isWaitingForDefenseCode],
  );

  // Handle hover sound for desktop (mouse enter) - trigger on any hover over game card
  const handleMouseEnter = useCallback(() => {
    // Play hover sound when hovering anywhere on the game card (matches old lobby)
    playHoverSound();
  }, [playHoverSound]);

  // PERFORMANCE OPTIMIZED: Memoize CSS classes to prevent re-computation
  const gameCardClasses = useMemo(
    () =>
      clsx(
        styles.gameCard,
        showTopGames && TopGamesNumbersStyles.gameContainer,
        showTopGames && TopGamesNumbersStyles[viewMode],
        'egt_game',
        `egtd_lobby_${imageUrls.orientation}`,
      ),
    [imageUrls.orientation, showTopGames, viewMode],
  );

  const infoBarClasses = useMemo(() => {
    const hasJackpotBand = Boolean(effectiveData.jackpotName) || Boolean(getNoJackpotException(data.gameFeatures));
    return clsx(
      styles.gameInfoBar,
      hasJackpotBand ? styles.hasJackpot : styles.noJackpot,
      !hasJackpotBand && 'without-jp',
    );
  }, [effectiveData.jackpotName, data.gameFeatures]);

  const badgeSlug = useMemo(
    () => getJackpotSlug(getNoJackpotException(data.gameFeatures) ?? effectiveData.jackpotName),
    [data.gameFeatures, effectiveData.jackpotName],
  );

  const showBadgePlate = useMemo(
    () => imageUrls.badgePlate && ALL_JP_BADGES.includes(badgeSlug),
    [imageUrls.badgePlate, badgeSlug],
  );

  return (
    <div
      className={gameCardClasses}
      data-view={viewMode}
      data-section={sectionName}
      data-cols={cols}
      data-rows={rows}
      onClick={handleClickMobile}
      onMouseEnter={handleMouseEnter}
      style={{
        ...getBadgeCSSVars(getBadgeSectionName(effectiveData, effectiveSectionName)),
        // the card's margin makes room for its ranking number, whose width depends on its digits
        ...(showTopGames && { '--top-number-left': getTopGameNumberLayout(topRank).left }),
      }}
    >
      {/* Main game image */}
      <GameThumbnail imageUrls={imageUrls} alt={data.name} loading="eager" />

      {/* Jackpot badge plate (background) */}
      {showBadgePlate && (
        <Image
          src={imageUrls.badgePlate as string}
          className={`badge_plate badge_plate_${badgeSlug} ${styles.badgePlate}`}
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          alt={effectiveData.jackpotName!}
          loading="eager"
        />
      )}

      {/* Jackpot badge */}
      <TagsContainer
        isSPJjackpot={SPJ_JACKPOT_GAMES.includes(effectiveData?.jackpotName ?? '')}
        shouldUseBrandingForBadges={shouldBranding.shouldUseBrandingForBadges}
      >
        {imageUrls.badge && (
          <Image
            src={imageUrls.badge}
            className={`badge badge_${badgeSlug}`}
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            alt={effectiveData.jackpotName!}
            loading="eager"
          />
        )}
      </TagsContainer>

      {/* Game info bottom container */}
      <div className={`${styles.gameBottomContainer} egt_game_bottom_container`}>
        {/* data-section must stay sectionName (not effectiveSectionName): the pickRandom bet-text sizing CSS keys off it; the Premium Link override would shrink it. */}
        <div
          data-view={viewMode}
          data-section={sectionName}
          className={infoBarClasses}
          data-jackpot={getJackpotSlug(
            getNoJackpotException(data.gameFeatures) ?? effectiveData.jackpotName ?? effectiveData.displayJackpotName,
          )}
        >
          <MinMaxBet currency={currency} data={effectiveData} />
          <CurrentPlayersCount currentPlayers={currentPlayers} />
        </div>
        {CRASH_GAMES.includes(data.name) ? (
          <CrashGameLabel />
        ) : (
          <JackpotBottomBand
            data={effectiveData}
            currency={currency}
            denomination={minDenomination}
            sectionName={effectiveSectionName}
            sectionGames={sectionGames}
          />
        )}
      </div>

      {/* Action buttons */}
      <ButtonPlay onClick={handleGameClick} className={`egt_game_btn_play ${styles.playButton}`} />
      <ButtonFavorites handleFavoriteClick={handleFavoriteClick} isFavorite={isFavorite} gameKey={data.gameKey} />

      <GameBanner gameData={data} sectionName={sectionName} />

      {/* Campaign indicator */}
      <CampaignLogo shouldShowTournamentCup={shouldShowTournamentCup} gameKey={data.gameKey} />

      {/* top games numbers */}
      {showTopGames && <TopGamesNumbers index={topRank.toString()} />}
    </div>
  );
};

// Memo comparison for Game. Uses reference equality on the `data` prop — in
// production, game objects are stable between API responses so this is a no-op
// most of the time. When a field on a game actually changes (e.g. the QA
// Panel's Mini Back-office editing topGame / gameFeatures / etc.), the
// controller spreads into a new game object and the new reference invalidates
// the memo.
//
// The previous version cherry-picked specific fields to compare — that
// over-specificity meant field mutations at runtime (like the QA Panel does)
// wouldn't trigger a re-render even though the data genuinely changed.
export default memo(Game, (prevProps, nextProps) => {
  if (prevProps.data !== nextProps.data) return false;
  else if (prevProps.sectionName !== nextProps.sectionName) return false;
  else if (prevProps.cols !== nextProps.cols) return false;
  else if (prevProps.rows !== nextProps.rows) return false;
  return true;
});
